import { ChangeDetectionStrategy, Component, Injector } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { EditPanelV2Component } from '@Work/Accounts/proprietary/controls/form-controls/edit-panel/edit-panel-v2.component';
import { MultiDocumentObjectService } from '@Core/models/proprietary/document/multidocument-object.service';
import { MultiDocumentObjectState } from '@Core/models/proprietary/document/multidocument-object.state';
import { SearchService } from '@Core/services/proprietary/search.service';

@Component({
    standalone: false,
    selector: 'ep-multi-document-file-edit-panel',
    templateUrl: './multi-document-file-edit-panel.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiDocumentFileEditPanelComponent extends EditPanelV2Component {
    ignorePageState = true; // overrides automatic readonly, error display mode from page state
    readOnlyMode = false; //override readonly mode

    @Select(MultiDocumentObjectState.documents)
    documents$: Observable<any>;

    constructor(
        private multiDocOS: MultiDocumentObjectService,
        private injector: Injector,
        private searchSrv: SearchService
    ) {
        super(injector);

        this.config = {
            objectService: this.multiDocOS,
            formMapping: [
                {
                    formGroupName: 'Filing',
                    nodeType: 'Filing',
                    updateMethod: (data: any) => {
                        // this.multiDocOS.updateFiling(data); //Special handling below
                    }
                }
            ]
        };

        this.form = new UntypedFormGroup(
            {
                Filing: this.fb.group({
                    Contract: this.fb.group({
                        Contract_ID: [''],
                        Contract_Num: ['']
                    }),
                    Claim: this.fb.group({
                        Claim_ID: [''],
                        Book_Claim_Num: ['']
                    }),
                    Entity: this.fb.group({
                        Entity_ID: [''],
                        Entity_Name: [''],
                        Role_Type: ['']
                    })
                })
            },
            { updateOn: 'blur' }
        );
    }

    claimSearchResult(data: any) {
        console.log(JSON.stringify(data));
        if (data) {
            data.forEach((claim) => {
                // update entity & contract on form
                this.form.controls.Filing.patchValue(
                    {
                        Entity: {
                            Role_Type: 'CN',
                            Entity_ID: claim.Consumer.Entity_ID,
                            Entity_Name: claim.Consumer.Entity_Name
                        },
                        Contract: {
                            Contract_ID: claim.Contract.Contract_ID,
                            Contract_Num: claim.Contract.Contract_Num
                        }
                    },
                    { emitEvent: false }
                );

                // update contract/claim references on existing new filings
                this.multiDocOS.updateFilingReferences({
                    Contract: {
                        Contract_ID: claim.Contract.Contract_ID,
                        Contract_Num: claim.Contract.Contract_Num
                    },
                    Claim: {
                        Claim_ID: claim.Claim_ID,
                        Book_Claim_Num: claim.Book_Claim_Num
                    }
                });

                // add filings to document state
                this.multiDocOS.addFiling({
                    Selected_Ind: 'Y',
                    Distribute: { Method: 'F' },
                    Entity: {
                        Role_Type: 'CN',
                        Entity_ID: claim.Consumer.Entity_ID,
                        Entity_Name: claim.Consumer.Entity_Name
                    },
                    Book: {
                        Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID
                    },
                    Book_Loc: {
                        Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID
                    },
                    Contract: {
                        Contract_ID: claim.Contract.Contract_ID,
                        Contract_Num: claim.Contract.Contract_Num
                    },
                    Claim: {
                        Claim_ID: claim.Claim_ID,
                        Book_Claim_Num: claim.Book_Claim_Num
                    }
                });
            });
        }
    }

    claimSearchCleared() {
        //clear claim search inputs

        // remove claim references on existing new filings
        this.multiDocOS.updateFilingReferences({
            Claim: {
                Claim_ID: null,
                Book_Claim_Num: null
            }
        });
    }

    contractSearchCleared() {
        //clear claim search inputs
        this.form.controls.Filing.patchValue(
            {
                Claim: {
                    Claim_ID: null,
                    Book_Claim_Num: null
                }
            },
            { emitEvent: false }
        );

        // remove contract/claim references on existing new filings
        this.multiDocOS.updateFilingReferences({
            Contract: {
                Contract_ID: null,
                Contract_Num: null
            },
            Claim: {
                Claim_ID: null,
                Book_Claim_Num: null
            }
        });
    }

    contractSearchResult(data: any[]) {
        if (data) {
            data.forEach((contract) => {
                if (contract.Consumer && contract.Consumer.Entity_ID > 0) {
                    // update entity on form
                    this.form.controls.Filing.patchValue(
                        {
                            Entity: {
                                Role_Type: 'CN',
                                Entity_ID: contract.Consumer.Entity_ID,
                                Entity_Name: contract.Consumer.Entity_Name
                            }
                        },
                        { emitEvent: false }
                    );
                    // clear claim on form
                    this.form.controls.Filing.patchValue(
                        {
                            Claim: {
                                Claim_ID: null,
                                Book_Claim_Num: null
                            }
                        },
                        { emitEvent: false }
                    );

                    // update contract/claim references on existing new filings
                    this.multiDocOS.updateFilingReferences({
                        Contract: {
                            Contract_ID: contract.Contract_ID,
                            Contract_Num: contract.Contract_Num
                        },
                        Claim: {
                            Claim_ID: null,
                            Book_Claim_Num: null
                        }
                    });

                    // add filings to document state
                    this.multiDocOS.addFiling({
                        Selected_Ind: 'Y',
                        Distribute: { Method: 'F' },
                        Entity: {
                            Role_Type: 'CN',
                            Entity_ID: contract.Consumer.Entity_ID,
                            Entity_Name: contract.Consumer.Entity_Name
                        },
                        Book: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID
                        },
                        Book_Loc: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID
                        },
                        Contract: {
                            Contract_ID: contract.Contract_ID,
                            Contract_Num: contract.Contract_Num
                        }
                    });
                }
                if (contract.Broker && contract.Broker.Entity_ID > 0) {
                    this.multiDocOS.addFiling({
                        Selected_Ind: 'N',
                        Distribute: { Method: 'F' },
                        Entity: {
                            Role_Type: 'BK',
                            Entity_ID: contract.Broker.Entity_ID,
                            Entity_Name: contract.Broker.Entity_Name
                        },
                        Book: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID
                        },
                        Book_Loc: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID
                        },
                        Contract: {
                            Contract_ID: contract.Contract_ID,
                            Contract_Num: contract.Contract_Num
                        }
                    });
                }
                if (contract.Supplier && contract.Supplier.Entity_ID > 0) {
                    this.multiDocOS.addFiling({
                        Selected_Ind: 'N',
                        Distribute: { Method: 'F' },
                        Entity: {
                            Role_Type: 'SP',
                            Entity_ID: contract.Supplier.Entity_ID,
                            Entity_Name: contract.Supplier.Entity_Name
                        },
                        Book: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID
                        },
                        Book_Loc: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID
                        },
                        Contract: {
                            Contract_ID: contract.Contract_ID,
                            Contract_Num: contract.Contract_Num
                        }
                    });
                }
            });
        }
    }

    entitySearchResult(data: any) {
        if (data) {
            data.forEach((entity) => {
                if (entity.Entity_ID > 0) {
                    this.multiDocOS.addFiling({
                        Selected_Ind: 'Y',
                        Distribute: { Method: 'F' },

                        Entity: {
                            Role_Type: this.getSelectedEntityRole(entity),
                            Entity_ID: entity.Entity_ID,
                            Entity_Name: entity.Entity_Name
                        },
                        Book: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID
                        },
                        Book_Loc: {
                            Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID
                        },
                        Contract: {
                            Contract_ID: this.getControlValue('Filing.Contract.Contract_ID'),
                            Contract_Num: this.getControlValue('Filing.Contract.Contract_Num')
                        }
                    });
                }
            });
        }
    }

    clearNewFilings() {
        //delete new filings from state
        this.multiDocOS.deleteNewFilings();

        //clear entity, contract, claim search inputs
        this.form.controls.Filing.patchValue(
            {
                Claim: {
                    Claim_ID: null,
                    Book_Claim_Num: null
                },
                Contract: {
                    Contract_ID: null,
                    Contract_Num: null
                },
                Entity: {
                    Entity_ID: null,
                    Entity_Name: null
                }
            },
            { emitEvent: false }
        );
    }

    getSelectedEntityRole(entity: any) {
        let selectedRole;
        const roles = entity?.Roles?.Role;

        if (roles && roles.length > 0) {
            for (const role of roles) {
                if ('Selected' in role && role.Selected) {
                    selectedRole = role.Role_Type;
                    break;
                }
            }

            if (!selectedRole) {
                selectedRole = roles[0].Role_Type;
            }
        }

        if (!selectedRole) {
            selectedRole = '';
        }

        return selectedRole;
    }
}
